import cx from "classnames";
import React from "react";

import { Link } from "@/mk2/components/Link";
import { HASHTAGS, HASHTAGS_SHOW_ALL } from "@/mk/autogenerated/translation";

import styles from "./HashtagsBox.mscss";

export interface HashtagWithCount {
    hashtag: string;
    count: number;
}

export interface BoxedHashtags {
    newest: string[];
    mostUsed: HashtagWithCount[];
}

interface OwnProps {
    hashtags: BoxedHashtags;
    createLink(hashtag: string);
}

type Props = OwnProps;

interface State {
    collapsed: boolean;
}

const Hashtag = ({ count, hashtag, url }) => {
    return (
        <Link to={url}>
            <div className={styles.HashtagsBox__hashtag}>
                <span className={styles.HashtagsBox__hashtag__name}>#{hashtag}</span>
                {count && (
                    <span className={styles.HashtagsBox__hashtag__count}>&nbsp;({count})</span>
                )}
            </div>
        </Link>
    );
};

const haveSameHashtags = (newest: string[], mostUsed: Array<{ hashtag: string }>): boolean => {
    if (newest.length !== mostUsed.length) {
        return false;
    }

    const newestSet = new Set(newest);
    if (newestSet.size !== newest.length) {
        return false; // sanity check, keby backend poslal duplicity
    }

    for (const { hashtag } of mostUsed) {
        if (!newestSet.has(hashtag)) {
            return false;
        }
    }

    return true;
};

class HashtagsBox extends React.PureComponent<Props, State> {
    private listRef: HTMLDivElement;

    constructor(props: Props) {
        super(props);
        this.state = {
            collapsed: true,
        };
    }

    public componentDidMount() {
        window.addEventListener("resize", this.update, false);
        this.run();
    }

    public componentDidUpdate() {
        this.run();
    }

    public componentWillUnmount() {
        window.removeEventListener("resize", this.update, false);
    }

    public render() {
        const { createLink, hashtags } = this.props;

        const showExpand = this.state.collapsed;
        const onlyMostUsed =
            hashtags.newest.length > 0 &&
            hashtags.mostUsed.length > 0 &&
            haveSameHashtags(hashtags.newest, hashtags.mostUsed);

        return (
            <div className={styles.HashtagsBox}>
                <div className={styles.HashtagsBox__body}>
                    <div className={styles.HashtagsBox__body__header}>
                        <div className={styles.HashtagsBox__body__header__caption}>{HASHTAGS}</div>
                        {showExpand && (
                            <div
                                className={styles.HashtagsBox__body__header__action}
                                onClick={this.handleExpandClick}
                            >
                                {HASHTAGS_SHOW_ALL}
                            </div>
                        )}
                    </div>

                    <div
                        ref={this.handleListRef}
                        className={cx(
                            styles.HashtagsBox__body__list,
                            showExpand && styles["HashtagsBox__body__list--collapsed"],
                        )}
                    >
                        {!onlyMostUsed && (
                            <>
                                {hashtags.newest.map((hashtag) => (
                                    <Hashtag
                                        key={hashtag}
                                        hashtag={hashtag}
                                        count={null}
                                        url={createLink(hashtag)}
                                    />
                                ))}

                                <div className={styles.HashtagsBox__bullet}>&bull;</div>
                            </>
                        )}

                        {hashtags.mostUsed.map((hashtagCount) => {
                            const { hashtag, count } = hashtagCount;
                            return (
                                <Hashtag
                                    key={hashtag}
                                    hashtag={hashtag}
                                    count={count}
                                    url={createLink(hashtag)}
                                />
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    }

    private handleListRef = (r: HTMLDivElement) => {
        this.listRef = r;
    };

    private run = () => {
        if (process.env.NODE_ENV === "test") {
            // Do not run in test environment
            return;
        }

        const element = this.listRef;
        if (!element) {
            return;
        }

        if (this.state.collapsed && element.scrollHeight === element.clientHeight) {
            this.setState({ collapsed: false });
        }
    };

    private update = () => {
        this.forceUpdate();
    };

    private handleExpandClick = () => {
        this.setState({ collapsed: false });
    };
}

export default HashtagsBox;
